import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, MapPin, Calendar, ArrowRight, Globe, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturedPackages = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    const fetchPkgs = async () => {
      try {
        const res = await axios.get(`${API}/packages?status=active&audience=agents`, { headers: { Authorization: `Bearer ${token}` } });
        // Show featured first, then latest, max 4
        const sorted = res.data.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        setPackages(sorted.slice(0, 4));
      } catch { /* silent */ }
    };
    fetchPkgs();
  }, [token]);

  if (packages.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="featured-packages-widget">
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-gray-700">Travel Packages</span>
        </div>
        <button onClick={() => navigate('/agent/packages')} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid="view-all-packages-btn">
          View All <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <div className="p-3 space-y-2">
        {packages.map(pkg => (
          <div key={pkg.id} className="group flex gap-3 p-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate('/agent/packages')} data-testid={`widget-pkg-${pkg.id}`}>
            <div className="w-16 h-14 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-50 overflow-hidden flex-shrink-0">
              {pkg.image_url ? (
                <img src={pkg.image_url} alt={pkg.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center"><MapPin className="w-5 h-5 text-emerald-200" /></div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-800 truncate group-hover:text-emerald-700">{pkg.title}</h4>
              <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                <Badge className={`text-[9px] px-1.5 py-0 ${pkg.trip_type === 'international' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
                  {pkg.trip_type === 'international' ? <Globe className="w-2.5 h-2.5 mr-0.5" /> : <Home className="w-2.5 h-2.5 mr-0.5" />}
                  {pkg.trip_type?.charAt(0).toUpperCase() + pkg.trip_type?.slice(1)}
                </Badge>
                <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{pkg.destination}</span>
                <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{pkg.duration_nights || 0}N</span>
              </div>
              <p className="text-xs font-bold text-emerald-700 mt-0.5">INR {(pkg.per_adult_price || pkg.price || 0).toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedPackages;
